import { withErrorBoundary } from '@sentry/react';
import React from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useQuery } from 'urql';
import config from '../../../config';
import { assets } from '../../assets';
import { BlurredContainerM2030, ImageCircleBig } from '../../components/containers/containers';
import DefaultScreen from '../../components/containers/default-screen';
import { VerticalSeparator } from '../../components/ui-utils/separators';
import { Fallback } from '../../components/utils/error-boundary';
import { GraphqlOperations } from '../../logic/graphql/operations';
import { Broker, Query } from '../../logic/graphql/types/types';
import { FormattedMessage, useIntl } from 'react-intl';

const InfoContainer = styled.div`
  width: 50%;
  padding-top: 20px;
`;

const ForwardIcon = styled.img`
  width: 10px;
  height: 10px;
  margin-left: 8px;
`;

const IconContainer = styled.div`
  width: 16px;
`;

const WarningIconContainer = styled.div`
  width: 18px;
`;

const phoneIcon = (
  <svg width="16" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8.5 0C8.91667 0 9.27083 0.145833 9.5625 0.4375C9.85417 0.729167 10 1.08333 10 1.5V14.5C10 14.9167 9.85417 15.2708 9.5625 15.5625C9.27083 15.8542 8.91667 16 8.5 16H1.5C1.08333 16 0.729167 15.8542 0.4375 15.5625C0.145833 15.2708 0 14.9167 0 14.5V1.5C0 1.08333 0.145833 0.729167 0.4375 0.4375C0.729167 0.145833 1.08333 0 1.5 0H8.5ZM4.28125 14.6875C4.48958 14.8958 4.72917 15 5 15C5.27083 15 5.5 14.8958 5.6875 14.6875C5.89583 14.5 6 14.2708 6 14C6 13.7292 5.89583 13.5 5.6875 13.3125C5.5 13.1042 5.27083 13 5 13C4.72917 13 4.48958 13.1042 4.28125 13.3125C4.09375 13.5 4 13.7292 4 14C4 14.2708 4.09375 14.5 4.28125 14.6875ZM8.5 11.625V1.875C8.5 1.625 8.375 1.5 8.125 1.5H1.875C1.625 1.5 1.5 1.625 1.5 1.875V11.625C1.5 11.875 1.625 12 1.875 12H8.125C8.375 12 8.5 11.875 8.5 11.625Z"
      fill="white"
    />
  </svg>
);

const emailIcon = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8 0.25C10.2083 0.25 12.0521 0.895833 13.5312 2.1875C15.0104 3.47917 15.75 5.16667 15.75 7.25C15.75 8.5625 15.3646 9.65625 14.5938 10.5312C13.8229 11.4062 12.6458 11.8438 11.0625 11.8438C10.75 11.8438 10.5104 11.8438 10.3438 11.8438C10.1771 11.8229 9.96875 11.7812 9.71875 11.7188C9.48958 11.6562 9.30208 11.5521 9.15625 11.4062C9.01042 11.2396 8.90625 11.0312 8.84375 10.7812C8.57292 11.1354 8.21875 11.4271 7.78125 11.6562C7.34375 11.8854 6.91667 12 6.5 12C5.58333 12 4.85417 11.7083 4.3125 11.125C3.77083 10.5208 3.5 9.70833 3.5 8.6875C3.5 7.3125 3.91667 6.17708 4.75 5.28125C5.60417 4.38542 6.625 3.9375 7.8125 3.9375C8.6875 3.9375 9.3125 4.16667 9.6875 4.625C9.70833 4.375 9.84375 4.25 10.0938 4.25H11.5C11.75 4.25 11.9479 4.34375 12.0938 4.53125C12.2396 4.69792 12.2917 4.90625 12.25 5.15625L11.5 8.9375C11.4375 9.20833 11.4271 9.42708 11.4688 9.59375C11.5312 9.76042 11.6875 9.85417 11.9375 9.875C12.4792 9.8125 12.9167 9.52083 13.25 9C13.5833 8.47917 13.75 7.89583 13.75 7.25C13.75 5.70833 13.1979 4.48958 12.0938 3.59375C10.9896 2.69792 9.625 2.25 8 2.25C6.41667 2.25 5.0625 2.8125 3.9375 3.9375C2.8125 5.0625 2.25 6.41667 2.25 8C2.25 9.58333 2.8125 10.9375 3.9375 12.0625C5.0625 13.1875 6.41667 13.75 8 13.75C9.125 13.75 10.1562 13.4479 11.0938 12.8438C11.2604 12.7396 11.4375 12.7083 11.625 12.75C11.8125 12.7708 11.9688 12.8542 12.0938 13L12.4062 13.375C12.5521 13.5625 12.6042 13.7708 12.5625 14C12.5208 14.2083 12.4062 14.375 12.2188 14.5C10.9479 15.3333 9.54167 15.75 8 15.75C5.85417 15.75 4.02083 15 2.5 13.5C1 11.9792 0.25 10.1458 0.25 8C0.25 5.85417 1 4.03125 2.5 2.53125C4.02083 1.01042 5.85417 0.25 8 0.25ZM7.3125 9.75C7.75 9.75 8.15625 9.54167 8.53125 9.125C8.92708 8.6875 9.125 8.14583 9.125 7.5C9.125 7.10417 9.03125 6.79167 8.84375 6.5625C8.65625 6.33333 8.38542 6.21875 8.03125 6.21875C7.55208 6.21875 7.125 6.4375 6.75 6.875C6.375 7.29167 6.1875 7.83333 6.1875 8.5C6.1875 8.89583 6.29167 9.20833 6.5 9.4375C6.70833 9.64583 6.97917 9.75 7.3125 9.75Z"
      fill="white"
    />
  </svg>
);

const Contact = (): JSX.Element => {
  const history = useHistory();
  const intl = useIntl();

  const getUserBrokersQueryResult = useQuery<Query>({
    query: GraphqlOperations.queries.getUserBrokers.queryString,
  })[0];

  const handleBackClick = (): void => {
    history.goBack();
  };

  const handleEmailClick = (email: string): void => {
    window.open('mailto:' + email);
  };

  const brokerCards = getUserBrokersQueryResult.data?.getUser?.brokers.map(
    (b: Broker, index: number) => (
      <BlurredContainerM2030 key={index} className="flex flex-col xl:flex-row justify-between">
        <div className="flex flex-col items-center">
          <ImageCircleBig
            src={b.img && config.backend_url + '/images/' + b.img}
            alt="Az ön brókere"
            className="mb-8"
            onError={(ev) => {
              ev.currentTarget.src = assets.images.profile_personal_data;
            }}
          />
          <span className="text-12">
            <FormattedMessage id="contactPage.yourBroker" />
          </span>{' '}
          <h2 className="text-12 text-center">{b.name}</h2>
        </div>
        <VerticalSeparator />
        <InfoContainer>
          <div className="flex flex-row items-center mb-4">
            <IconContainer>{phoneIcon}</IconContainer>
            <span className="text-12 ml-2">
              <b>{b.phone}</b>
            </span>
          </div>
          <div
            className="flex flex-row items-center cursor-pointer mb-4"
            onClick={() => handleEmailClick(b.email)}
          >
            <IconContainer>{emailIcon}</IconContainer>
            <span className="text-12 ml-2">
              <b>{b.email}</b>
            </span>
            <ForwardIcon src={assets.icons.forward_menu} />
          </div>
        </InfoContainer>
      </BlurredContainerM2030>
    ),
  );

  return (
    <DefaultScreen
      background={assets.images.contact_bg}
      title={intl.formatMessage({ id: 'contactPage.title' })}
      onBackClick={handleBackClick}
      loading={getUserBrokersQueryResult.fetching}
    >
      <div className="mb-3">
        <BlurredContainerM2030>
          <div className="flex flex-col lg:flex-row items-center">
            <WarningIconContainer className="mb-2 lg:mb-0">
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.2857 14.3828C10.2857 14.5636 10.1451 14.7143 9.96429 14.7143H8.03571C7.85491 14.7143 7.71429 14.5636 7.71429 14.3828V12.4743C7.71429 12.2935 7.85491 12.1429 8.03571 12.1429H9.96429C10.1451 12.1429 10.2857 12.2935 10.2857 12.4743V14.3828ZM10.2656 10.6261C10.2556 10.7567 10.1049 10.8571 9.92411 10.8571H8.06585C7.875 10.8571 7.72433 10.7567 7.72433 10.6261L7.55357 6.03571C7.55357 5.97545 7.58371 5.875 7.65402 5.82478C7.71429 5.77455 7.80469 5.71429 7.89509 5.71429H10.1049C10.1953 5.71429 10.2857 5.77455 10.346 5.82478C10.4163 5.875 10.4464 5.95536 10.4464 6.01562L10.2656 10.6261ZM10.125 1.24442C9.90402 0.832589 9.4721 0.571427 9 0.571427C8.5279 0.571427 8.09598 0.832589 7.875 1.24442L0.160714 15.3873C-0.0602679 15.779 -0.0502232 16.2612 0.180804 16.6529C0.41183 17.0446 0.833705 17.2857 1.28571 17.2857H16.7143C17.1663 17.2857 17.5882 17.0446 17.8192 16.6529C18.0502 16.2612 18.0603 15.779 17.8393 15.3873L10.125 1.24442Z"
                  fill="#EEBC64"
                />
              </svg>
            </WarningIconContainer>
            <h2 className="text-warning lg:ml-3">
              <FormattedMessage id="contactPage.warningTitle" />
            </h2>
          </div>
          <p className="text-12">
            <FormattedMessage id="contactPage.warningDescription" />
          </p>
        </BlurredContainerM2030>
      </div>
      <div className="grid row-gap-1 col-gap-3  grid-cols-1 lg:grid-cols-2">{brokerCards}</div>
    </DefaultScreen>
  );
};

export default withErrorBoundary(Contact, { fallback: Fallback });
